/* Course plotting readout.
 *
 * The live feasibility estimate is the point of this panel. Command PE offers
 * estimation only inside its Operation Planner; putting it on an ordinary course means
 * the operator learns a plan is not flyable *while drawing it*, not after committing.
 */
import { AlertTriangle, CornerUpLeft, Fuel, Route, Trash2 } from 'lucide-react'
import { useDeck } from '../state/store'
import { canPlotCourse, hoursToHm, planCourse, resolveCourseTarget } from '../lib/course'
import { bearing, nm } from '../lib/format'

export function CoursePlotter() {
  const tool = useDeck((s) => s.tool)
  const courses = useDeck((s) => s.courses)
  const courseFocus = useDeck((s) => s.courseFocus)
  const entities = useDeck((s) => s.entities)
  const selection = useDeck((s) => s.selection)
  const undo = useDeck((s) => s.courseUndo)
  const clear = useDeck((s) => s.courseClear)
  const select = useDeck((s) => s.select)

  if (tool !== 'course') return null

  // Same rule as the map click handler, from the same function so the two cannot drift.
  const { entityId, waypoints } = resolveCourseTarget(selection, courses, courseFocus)
  const entity = entities.find((e) => e.id === entityId) ?? null

  // Other units that already have a plan. Courses persist per unit now, so without this the
  // operator has no way to know the other plans still exist — persistence the operator cannot
  // see is indistinguishable from the data loss this replaced.
  const otherPlans = Object.keys(courses)
    .filter((id) => id !== entityId && courses[id].length > 0)
    .sort()
    .map((id) => ({ id, entity: entities.find((e) => e.id === id) }))
    .filter((o): o is { id: string; entity: NonNullable<typeof o.entity> } => Boolean(o.entity))

  if (!entity) {
    return (
      <div className="panel absolute left-3 top-3 z-20 w-64 overflow-hidden">
        <div className="flex items-center gap-1.5 px-3 py-2" style={{ borderBottom: '1px solid var(--color-line-2)' }}>
          <Route size={12} aria-hidden />
          <span className="title">Plot course</span>
        </div>
        <p className="px-3 py-2" style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>
          Select a unit, then click on the map to add waypoints.
        </p>
      </div>
    )
  }

  if (!canPlotCourse(entity)) {
    return (
      <div className="panel absolute left-3 top-3 z-20 w-64 overflow-hidden">
        <div className="flex items-center gap-1.5 px-3 py-2" style={{ borderBottom: '1px solid var(--color-line-2)' }}>
          <Route size={12} aria-hidden />
          <span className="title">Plot course</span>
        </div>
        <p className="px-3 py-2" style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>
          <span className="num">{entity.designation}</span> cannot be given a course — it is a
          fixed installation.
        </p>
      </div>
    )
  }

  const plan = planCourse(entity, waypoints)
  const hasLegs = plan.legs.length > 0

  return (
    <div className="panel absolute left-3 top-3 z-20 w-72 overflow-hidden">
      <div
        className="flex items-center justify-between px-3 py-2"
        style={{ borderBottom: '1px solid var(--color-line-2)' }}
      >
        <span className="title flex items-center gap-1.5">
          <Route size={12} aria-hidden />
          Course · {entity.designation}
        </span>
        <div className="flex items-center gap-1">
          <button
            className="ctl"
            style={{ width: 20, height: 20, padding: 0 }}
            onClick={() => undo(entity.id)}
            disabled={!hasLegs}
            aria-label="Undo last waypoint"
            title="Undo last waypoint"
          >
            <CornerUpLeft size={11} />
          </button>
          <button
            className="ctl"
            style={{ width: 20, height: 20, padding: 0 }}
            onClick={() => clear(entity.id)}
            disabled={!hasLegs}
            aria-label="Clear course"
            title="Clear course  (Esc)"
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>

      {!hasLegs && (
        <>
          <div className="row">
            <span className="label" style={{ fontSize: 9 }}>Speed</span>
            <span className="num" style={{ fontSize: 12 }}>{entity.speedKt}<span className="unit">kt</span></span>
          </div>
          <div className="row">
            <span className="label" style={{ fontSize: 9 }}>Endurance left</span>
            <span className="num" style={{ fontSize: 12 }}>
              {hoursToHm((entity.enduranceHrs ?? 0) * entity.fuelFraction)}
            </span>
          </div>
          <div className="row" style={{ borderBottom: 'none' }}>
            <span className="label" style={{ fontSize: 9 }}>Range left</span>
            <span className="num" style={{ fontSize: 12 }}>
              {nm(plan.rangeRemainingNm, 0)}<span className="unit">nm</span>
            </span>
          </div>
          <p className="px-3 py-2" style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>
            Click on the map to add waypoints.
          </p>
        </>
      )}

      {hasLegs && (
        <>
          {/* Per-leg breakdown */}
          <div className="max-h-52 overflow-y-auto">
            {plan.legs.map((l) => (
              <div
                key={l.index}
                className="flex items-center gap-2 px-3 py-1"
                style={{
                  borderBottom: '1px solid var(--color-line-1)',
                  background: l.reachable ? undefined : 'rgba(232,81,90,0.09)',
                }}
              >
                <span className="label shrink-0" style={{ fontSize: 8.5, minWidth: 18 }}>
                  {l.index + 1}
                </span>
                <span className="num shrink-0" style={{ fontSize: 11, minWidth: 38 }}>
                  {bearing(l.bearingDeg)}
                </span>
                <span className="num shrink-0" style={{ fontSize: 11, minWidth: 52 }}>
                  {nm(l.distanceNm)}<span className="unit">nm</span>
                </span>
                <span className="num flex-1" style={{ fontSize: 11, color: 'var(--color-text-2)' }}>
                  {hoursToHm(l.hours)}
                </span>
                <span
                  className="num shrink-0"
                  style={{
                    fontSize: 10.5,
                    color: !l.reachable
                      ? 'var(--color-crit)'
                      : l.fuelRemaining < 0.2
                        ? 'var(--color-warn)'
                        : 'var(--color-text-3)',
                  }}
                  title="Fuel fraction remaining on arrival at this waypoint"
                >
                  {Math.round(l.fuelRemaining * 100)}%
                </span>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="row" style={{ background: 'var(--color-surface-2)' }}>
            <span className="label" style={{ fontSize: 9 }}>Total</span>
            <span className="num" style={{ fontSize: 12 }}>
              {nm(plan.totalNm)}<span className="unit">nm</span>
              <span style={{ color: 'var(--color-text-3)' }}> · </span>
              {hoursToHm(plan.totalHours)}
            </span>
          </div>

          {/* Feasibility — the whole point of the panel */}
          {plan.feasible ? (
            <div
              className="flex items-start gap-2 px-3 py-2"
              style={{ background: 'rgba(79,196,138,0.08)' }}
            >
              <Fuel size={12} style={{ color: 'var(--color-ok)', marginTop: 1 }} aria-hidden />
              <p style={{ fontSize: 11, color: 'var(--color-text-2)', lineHeight: 1.4 }}>
                Sustainable —{' '}
                <span className="num" style={{ color: 'var(--color-ok)' }}>
                  {Math.round(plan.fuelAtEnd * 100)}%
                </span>{' '}
                fuel on arrival,{' '}
                <span className="num">{nm(plan.rangeRemainingNm - plan.totalNm, 0)} nm</span> of
                range to spare.
              </p>
            </div>
          ) : (
            <div
              className="flex items-start gap-2 px-3 py-2"
              style={{ background: 'rgba(232,81,90,0.10)' }}
            >
              <AlertTriangle
                size={12}
                style={{ color: 'var(--color-crit)', marginTop: 1 }}
                aria-hidden
              />
              <p style={{ fontSize: 11, color: 'var(--color-text-2)', lineHeight: 1.4 }}>
                <span style={{ color: 'var(--color-crit)', fontWeight: 500 }}>
                  Not achievable.
                </span>{' '}
                Fuel exhausted on leg{' '}
                <span className="num">{(plan.bingoAtLeg ?? 0) + 1}</span> —{' '}
                <span className="num">{nm(plan.totalNm - plan.rangeRemainingNm, 0)} nm</span> beyond
                remaining range.
              </p>
            </div>
          )}

          <p
            className="px-3 py-1.5"
            style={{ fontSize: 9.5, color: 'var(--color-text-3)', borderTop: '1px solid var(--color-line-1)' }}
          >
            Planning estimate at current speed. Real burn varies with throttle, altitude and
            configuration.
          </p>
        </>
      )}
      {otherPlans.length > 0 && (
        <div
          className="px-3 py-1.5"
          style={{ borderTop: '1px solid var(--color-line-1)' }}
        >
          <span className="label" style={{ fontSize: 8.5 }}>
            Also plotted
          </span>
          <div className="mt-1 flex flex-wrap gap-1">
            {otherPlans.map((o) => (
              <button
                key={o.id}
                className="ctl"
                style={{ height: 18, padding: '0 6px', fontSize: 9.5 }}
                onClick={() => select({ kind: 'entity', id: o.id })}
                title={`Switch to ${o.entity.designation}'s course (${courses[o.id].length} legs)`}
              >
                {o.entity.designation}
                <span style={{ color: 'var(--color-text-3)' }}> · {courses[o.id].length}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
